/**
 * useAISummary Hook
 *
 * Thread summarization with caching via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailAISummarizeResult } from '@/lib/api';
import { errorMessage } from '../types';

export function useAISummary(accountId: string | undefined, threadId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ['email', 'ai', 'summary', threadId];

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      if (!accountId || !threadId) throw new Error('Missing params');
      return api.aiSummarizeEmailThread(accountId, threadId);
    },
    enabled: false, // Only fetch on demand
    staleTime: Infinity, // Cache indefinitely until manual refresh
  });

  const summarize = useCallback(async () => {
    if (!accountId || !threadId) return;
    await queryClient.fetchQuery({
      queryKey,
      queryFn: () => api.aiSummarizeEmailThread(accountId!, threadId!),
    });
  }, [accountId, threadId, queryClient, queryKey]);

  const refresh = useCallback(async () => {
    queryClient.removeQueries({ queryKey });
    await summarize();
  }, [queryClient, queryKey, summarize]);

  return {
    summary: queryResult.data ?? (null as EmailAISummarizeResult | null),
    loading: queryResult.isFetching,
    error: errorMessage(queryResult.error),
    summarize,
    refresh,
  };
}
